import * as React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { clientsApi } from '../api/clients.api';
import { personClientSchema, type PersonClientFormData } from '../schemas';
import { removeEmptyFields } from '@/lib/utils';

export function usePersonForm() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  let { id } = useParams();
  const [searchParams] = useSearchParams();
  const manage = searchParams.get('manage');

  if (id === 'null') id = undefined;

  const form = useForm<PersonClientFormData>({
    resolver: zodResolver(personClientSchema),
    defaultValues: {
      type: 'PERSON',
      name: '',
      lastname: '',
      middlename: '',
      pinfl: '',
      email: '',
      phone: '',
      country: '',
      region: '',
      city: '',
      street: '',
      zip: '',
      companyId: undefined,
      birthday: undefined,
    },
  });

  const { isSubmitting } = form.formState;

  // Load existing client data for edit
  const { mutate: loadClient } = useMutation({
    mutationFn: clientsApi.getById,
    onSuccess: (data) => {
      form.reset({
        type: 'PERSON',
        name: data.name || '',
        lastname: data.lastname || '',
        middlename: data.middlename || '',
        pinfl: data.pinfl || '',
        email: data.email || '',
        phone: data.phone || '',
        country: data.country || '',
        region: data.region || '',
        city: data.city || '',
        street: data.street || '',
        zip: data.zip || '',
        companyId: data.companyId || undefined,
        birthday: data.birthday ? new Date(data.birthday) : undefined,
      });
    },
    onError: (err: Error) => {
      toast.error(`${t('toast.error')}: ${err.message}`);
    },
  });

  // Create mutation
  const { mutate: createClient } = useMutation({
    mutationFn: clientsApi.create,
    onSuccess: () => {
      toast.success(t('toast.createdSuccess', { item: t('person') }));
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      form.reset();
      navigateBack();
    },
    onError: (err: Error) => {
      toast.error(t('toast.errorCreating', { item: t('person'), message: err.message }));
    },
  });

  // Update mutation
  const { mutate: updateClient } = useMutation({
    mutationFn: ({ id, data }: { id: string; data: PersonClientFormData }) =>
      clientsApi.update(id, data),
    onSuccess: () => {
      toast.success(t('toast.updatedSuccess', { item: t('person') }));
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigateBack();
    },
    onError: (err: Error) => {
      toast.error(t('toast.errorUpdating', { item: t('person'), message: err.message }));
    },
  });

  React.useEffect(() => {
    if (id) loadClient(id);
  }, [id, loadClient]);

  const navigateBack = () => {
    if (manage) {
      navigate('/app/manage');
    } else {
      navigate('/app/clients');
    }
  };

  const onSubmit = (values: PersonClientFormData, action = 'create') => {
    const cleanedValues = removeEmptyFields(values);

    if (action === 'update' && id) {
      updateClient({ id, data: cleanedValues as PersonClientFormData });
    } else {
      createClient(cleanedValues as PersonClientFormData);
    }
  };

  return {
    form,
    isSubmitting,
    onSubmit,
    navigateBack,
    id,
    manage,
  };
}
